import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from '@nestle-npus-nbm/pds-ui';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';

import StorageAppLinkBundle from 'src/cms-components/StorageAppLinkBundle/StorageAppLinkBundle';
import StorageBrazeCard from 'src/cms-components/StorageBrazeCard/StorageBrazeCard';
import { NerdMenuElementType } from 'src/common/enums';
// import { useBrazeContext } from 'src/hooks/useBraze/useBraze';
import { isServer } from 'src/utils/environment';
import { BaseLink } from '../Link';
import NerdMenuLinkListWrapper from './NerdMenuLinkListWrapper';

type NerdMenuStackedProps = {
  nerdMenu: NerdMenu;
};

const NerdMenuStackedComponent: React.FC<{
  componentElement: NerdMenuElement;
}> = ({ componentElement }) => {
  // TODO: Implement braze context for header menu
  // const { brazeCardPayloads } = useBrazeContext();

  const component = componentElement?.component;

  if (component?.type === 'storage__braze_card') {
    return (
      <div className="pds-px-5 pds-pb-4.5 pds-pt-4">
        {/* <StorageBrazeCard defaultCard={component} brazeCard={brazeCardPayloads[0]} /> */}
        <StorageBrazeCard defaultCard={component} />
      </div>
    );
  }

  if (!isServer()) {
    // eslint-disable-next-line no-console
    console.error('Unknown component type', componentElement);
  }

  return null;
};

const NerdMenuStackedColumnComponent: React.FC<{
  componentElement: NerdMenuElement;
}> = ({ componentElement }) => {
  const { component } = componentElement;

  if (!component) {
    if (!isServer()) {
      // eslint-disable-next-line no-console
      console.error('No component found in element', componentElement);
    }

    return null;
  }

  return (
    <div className="pds-flex pds-flex-col pds-gap-3 pds-rounded pds-bg-surface-layer pds-p-4">
      <h3 className="pds-m-0 pds-text-body-md pds-font-extrabold">{componentElement.title}</h3>

      {component?.type === 'storage__app_link_bundle' && (
        <StorageAppLinkBundle
          node={component}
          analyticsEvent={{
            event: 'nav_click',
            eventParams: {
              nav_type: 'header',
              nav_item: componentElement.title,
            },
          }}
        />
      )}
    </div>
  );
};

const NerdMenuStacked: React.FC<NerdMenuStackedProps> = ({ nerdMenu }) => {
  const topLevelMenuItems = nerdMenu.menuElements.filter(
    element =>
      element.type === NerdMenuElementType.MEGA_MENU_DROPDOWN ||
      element.type === NerdMenuElementType.MEGA_MENU_LINK,
  );

  if (topLevelMenuItems.length === 0) {
    // eslint-disable-next-line no-console
    console.error('There are no top-level menu items provided. Please update the menu config.');
    return null;
  }

  return (
    <div>
      <Accordion type="single" collapsible options={{ headingLevel: 'h2' }} asChild>
        <ul className="pds-m-0 pds-p-0">
          {topLevelMenuItems.map((topLevelMenuItem, index) => {
            if (topLevelMenuItem.type === NerdMenuElementType.MEGA_MENU_DROPDOWN) {
              const linkListWrappers =
                topLevelMenuItem.menuElements
                  ?.filter(element => element.type === NerdMenuElementType.COLUMN)
                  .flatMap(x => x.menuElements || [])
                  .filter(x => x.type === NerdMenuElementType.LINK_LISTS_WRAPPER) || [];

              // Components inside of a column
              const columnComponents =
                topLevelMenuItem.menuElements
                  ?.filter(element => element.type === NerdMenuElementType.COLUMN)
                  .flatMap(x => x.menuElements || [])
                  .filter(x => x.type === NerdMenuElementType.COMPONENT) || [];

              // Component that is a sibling of the column
              const columnSiblingComponent = topLevelMenuItem.menuElements?.filter(
                element => element.type === NerdMenuElementType.COMPONENT,
              )[0];

              if (linkListWrappers.length === 0 && !columnSiblingComponent) {
                // eslint-disable-next-line no-console
                console.error('No link list wrappers or components found', topLevelMenuItem);
                return null;
              }

              return (
                <AccordionItem value={index.toString()} key={index} asChild>
                  <li className="last:pds-border-b-none [&_>_*:first-child]:pds-mb-0">
                    <AccordionTrigger
                      className="pds-relative pds-pb-3.5 pds-pr-4 pds-pt-4.5 pds-text-title-md pds-font-extrabold focus-visible:pds-outline-offset-2 focus-visible:pds-outline-focus"
                      onClick={() => {
                        sendIt({
                          event: 'nav_click',
                          eventParams: {
                            nav_item: topLevelMenuItem.title,
                            nav_type: 'header',
                          },
                        });
                      }}
                    >
                      {topLevelMenuItem.title}
                    </AccordionTrigger>

                    <AccordionContent
                      className="pds-flex pds-flex-col pds-gap-4 pds-pb-0"
                      role={undefined}
                      forceMount
                    >
                      <Accordion type="single" collapsible options={{ headingLevel: 'h3' }} asChild>
                        <ul className="pds-m-0 pds-flex pds-flex-col pds-gap-3 pds-p-0">
                          {linkListWrappers.map((linkListWrapper, innerIndex) => {
                            // Use the first link list heading as the title with fallback to the wrapper title
                            const nestedAccordionTriggerTitle =
                              linkListWrapper.menuElements?.filter(
                                element => element.type === NerdMenuElementType.LINK_LIST_HEADING,
                              )[0]?.title || linkListWrapper.title;

                            return (
                              <AccordionItem
                                value={`${index.toString()}-${innerIndex.toString()}`}
                                key={linkListWrapper.title}
                                asChild
                              >
                                <li className="pds-rounded pds-border-none pds-bg-surface-layer [&_>_*:first-child]:pds-mb-0">
                                  <AccordionTrigger
                                    className="pds-px-4 pds-py-4.5 pds-pr-4 pds-text-body-md pds-font-extrabold focus-visible:-pds-outline-offset-1 focus-visible:pds-outline-focus"
                                    onClick={() => {
                                      sendIt({
                                        event: 'nav_click',
                                        eventParams: {
                                          nav_item: linkListWrapper.title,
                                          nav_type: 'header',
                                        },
                                      });
                                    }}
                                  >
                                    {nestedAccordionTriggerTitle}
                                  </AccordionTrigger>

                                  <AccordionContent
                                    role={undefined}
                                    forceMount
                                    className="pds-pb-0"
                                  >
                                    <div className="pds-flex pds-flex-col pds-gap-4">
                                      <NerdMenuLinkListWrapper linkListWrapper={linkListWrapper} />
                                    </div>
                                  </AccordionContent>
                                </li>
                              </AccordionItem>
                            );
                          })}
                        </ul>
                      </Accordion>

                      {columnComponents.length > 0 && (
                        <div className="pds-flex pds-gap-4">
                          {columnComponents.map(componentElement => {
                            return (
                              <div key={componentElement.title} className="pds-grow">
                                <NerdMenuStackedColumnComponent
                                  componentElement={componentElement}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {columnSiblingComponent && (
                        <NerdMenuStackedComponent componentElement={columnSiblingComponent} />
                      )}
                    </AccordionContent>
                  </li>
                </AccordionItem>
              );
            }

            if (topLevelMenuItem.type === NerdMenuElementType.MEGA_MENU_LINK) {
              return (
                <AccordionItem
                  value={index.toString()}
                  key={index}
                  className="pds-group pds-border-b-none"
                  asChild
                >
                  <li>
                    <AccordionHeader>
                      <BaseLink
                        to={topLevelMenuItem.link.url}
                        key={index}
                        analytics={false}
                        className="pds-block pds-border-b pds-py-4.5 pds-pr-4 pds-text-title-md pds-font-extrabold pds-text-black pds-no-underline group-[&:last-child]:pds-border-b-none"
                      >
                        {topLevelMenuItem.title}
                      </BaseLink>
                    </AccordionHeader>
                  </li>
                </AccordionItem>
              );
            }

            // eslint-disable-next-line no-console
            console.error(`Unsupported menu element type: ${topLevelMenuItem.type}`);
            return null;
          })}
        </ul>
      </Accordion>
    </div>
  );
};

export default NerdMenuStacked;
